import type { OnDestroy, OnInit, OnChanges, SimpleChanges } from '@angular/core'
import { Component, ChangeDetectionStrategy, input, output, signal } from '@angular/core'
import { Color } from '@awork/_shared/types/color'
import { DotsLoaderComponent } from '../../../ui-help/dots-loader/dots-loader.component'
import { FileIconComponent } from '../../file-icon/file-icon.component'
import { Size } from '@awork/_shared/types/size'
import type { FilePreviewItem } from '../types'

@Component({
  selector: 'aw-file-preview-lightbox-thumbnail',
  templateUrl: './file-preview-lightbox-thumbnail.component.html',
  styleUrls: ['./file-preview-lightbox-thumbnail.component.scss'],
  imports: [DotsLoaderComponent, FileIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilePreviewLightboxThumbnailComponent implements OnInit, OnChanges, OnDestroy {
  private static readonly IMAGE_LOAD_TIMEOUT = 10_000
  readonly item = input.required<FilePreviewItem>()
  readonly imageLoadComplete = output<void>()

  readonly isLoading = signal(false)
  protected readonly Color = Color
  protected readonly Size = Size
  readonly view = signal<'image' | 'icon'>('icon')
  private imageLoadTimeoutId: number | null = null

  ngOnInit(): void {
    this.initImage()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.item?.previousValue?.thumbnailUrl !== changes.item?.currentValue?.thumbnailUrl) {
      this.initImage()
    }
  }

  ngOnDestroy(): void {
    this.clearImageLoadTimeout()
  }

  /**
   * Inits the image view
   */
  initImage(): void {
    this.clearImageLoadTimeout()

    const item = this.item()

    if (item.previewKind === 'image' && item.thumbnailUrl) {
      this.isLoading.set(true)

      this.view.set('image')
      this.imageLoadTimeoutId = window.setTimeout(
        () => this.imageLoadingError(),
        FilePreviewLightboxThumbnailComponent.IMAGE_LOAD_TIMEOUT
      )
    } else {
      this.view.set('icon')
    }
  }

  /**
   * Show Image after loading successfully and hide file Icon
   */
  imageLoaded(): void {
    this.clearImageLoadTimeout()
    this.isLoading.set(false)
    this.imageLoadComplete.emit()
  }

  /**
   * Handle broken images and show file Icon
   */
  imageLoadingError(): void {
    this.clearImageLoadTimeout()
    this.isLoading.set(false)
    this.view.set('icon')
  }

  /**
   * Clears the pending fallback for an image that has not settled.
   */
  private clearImageLoadTimeout(): void {
    if (this.imageLoadTimeoutId === null) {
      return
    }

    window.clearTimeout(this.imageLoadTimeoutId)
    this.imageLoadTimeoutId = null
  }

  /**
   * Gets the selected item's lower-case file extension.
   * @returns {string} File extension without a leading dot.
   */
  protected getFileExtension(): string {
    return this.item().name.split('.').pop()?.toLowerCase() ?? ''
  }
}
